function PrivacyPageComponent() {
    // Replace these placeholders with your actual information
    const siteName = "My Game Group";
    const contactEmail = "privacy@mygame.group";

    return (
        <section className="section">
            <div className="container">
                <div className="content">
                    <h1 className="title is-3">Privacy Policy for {siteName}</h1>
                    <h3 className="subtitle is-6"><strong>Effective Date:</strong> July 13, 2025</h3>
                    <p>
                        This Privacy Policy describes how {siteName} ("we", "us", or "our")
                        collects, uses, and handles your information when you use our website
                        and services to track board game scores. Your privacy is important
                        to us, and we are committed to protecting it.
                    </p>

                    <h2 className="title is-4 mt-5">1. Information We Collect</h2>
                    <p>
                        We collect information in two ways: information you give us directly
                        and limited technical information that is collected automatically.
                    </p>
                    <h3 className="title is-5 mt-4">A. Information You Provide to Us:</h3>
                    <ul>
                        <li>
                            <strong>Account Information:</strong> When you create an account, we
                            collect your Display Name and Email Address. This information is
                            collected whether you sign up directly with an email and password
                            or through the "Sign in with Google" service.
                        </li>
                        <li>
                            <strong>User-Generated Content:</strong> You and other members of
                            your groups generate data by using the service. This includes
                            group names, member names (including non-registered members),
                            games played, scores for each game, and comments on plays.
                        </li>
                    </ul>
                    <h3 className="title is-5 mt-4">B. Information Collected Automatically:</h3>
                    <ul>
                        <li>
                            <strong>Necessary Application Data:</strong> Our site uses Google
                            Firebase for its core functionality. Firebase automatically uses
                            your browser's <code>IndexedDB</code> storage to manage essential
                            operations like keeping your session active, ensuring the security
                            of our services (via App Check), and managing a unique identifier
                            for your app installation. This storage is strictly necessary for
                            the site to function securely.
                        </li>
                    </ul>

                    <h2 className="title is-4 mt-5">2. How We Use Your Information</h2>
                    <p>
                        We use the information we collect for the following purposes:
                    </p>
                    <ul>
                        <li>To provide and maintain the service (tracking scores).</li>
                        <li>To create and manage your user account.</li>
                        <li>To calculate and display game statistics for you and your groups.</li>
                        <li>To communicate with you regarding data requests or important account information.</li>
                    </ul>

                    <h2 className="title is-4 mt-5">3. How We Share Your Information</h2>
                    <p>
                        We do not sell or share your personal information with third-party
                        companies for their marketing or advertising purposes.
                    </p>
                    <ul>
                        <li>
                            <strong>Service Providers:</strong> We use Google Firebase as our
                            backend service provider for database hosting, authentication, and
                            other core functions. Your data is stored on Google's secure servers.
                        </li>
                        <li>
                            <strong>Within Your Groups:</strong> Your Display Name and game activity
                            are visible to other members of the private groups you belong to, as this
                            is the core function of the site.
                        </li>
                    </ul>

                    <h2 className="title is-4 mt-5">4. Cookies and Tracking Technologies</h2>
                    <p>We believe in a simple and transparent approach to privacy.</p>
                    <ul>
                        <li>
                            <strong>Our Site:</strong> Our website does not set any first-party
                            cookies for advertising, analytics, or tracking.
                        </li>
                        <li>
                            <strong>Necessary Storage:</strong> We use your browser's{" "}
                            <code>localStorage</code> for one specific purpose: to remember the
                            choice you make on our cookie consent banner. This is considered
                            strictly necessary.
                        </li>
                        <li>
                            <strong>Third-Party Cookies (Google Sign-In):</strong> If you
                            choose to use the "Sign in with Google" feature, our partner
                            (Google) will set third-party cookies on your device. These cookies
                            are used by Google for authentication and may also be used for their
                            own personalization and advertising purposes. We require your explicit
                            consent before enabling this feature.
                        </li>
                    </ul>

                    <h2 className="title is-4 mt-5">5. Your Data Rights (GDPR)</h2>
                    <p>We respect your right to control your personal data.</p>
                    <ul>
                        <li>
                            <strong>Right of Access:</strong> You have the right to request a
                            copy of all the personal data we hold about you. You can make a
                            "Get My Data" request from your account settings.
                        </li>
                        <li>
                            <strong>Right to Rectification:</strong> You can edit your Display
                            Name and other profile information at any time.
                        </li>
                        <li>
                            <strong>Right to Erasure (Deletion):</strong> You have the right to
                            delete your account. When you request deletion, we will delete your
                            account and profile, and anonymize your game and score data by
                            replacing your name with 'Deleted User.' This allows us to
                            maintain the historical integrity of your groups without retaining
                            your personal data. Once this process is complete, it cannot be
                            undone. If you choose to rejoin later, you will need to create a
                            new account.
                        </li>
                        <li>
                            <strong>Managing Cookie Consent:</strong> You can change your cookie
                            consent preferences at any time by clicking the "Manage Cookie
                            Preferences" link in the site's footer.
                        </li>
                    </ul>

                    <h2 className="title is-4 mt-5">6. Data Retention</h2>
                    <p>We keep your personal data only as long as necessary.</p>
                    <ul>
                        <li>
                            <strong>Account and Game Data:</strong> We retain this data as long as
                            your account is active.
                        </li>
                        <li>
                            <strong>Anonymized Data:</strong> Anonymized game and score data is
                            kept indefinitely as part of the historical record of a group.
                        </li>
                        <li>
                            <strong>Compliance Logs:</strong> Records of your data access or deletion
                            requests are kept for a limited period (e.g., 2 years) to comply with
                            our legal obligations.
                        </li>
                    </ul>

                    <h2 className="title is-4 mt-5">7. Children's Privacy</h2>
                    <p>
                        Our service is not directed to individuals under the age of 16, and
                        we do not knowingly collect personal data from them.
                    </p>

                    <h2 className="title is-4 mt-5">8. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this policy from time to time. If we make significant
                        changes, we will notify you through the service or by email. We
                        encourage you to review this policy periodically.
                    </p>

                    <h2 className="title is-4 mt-5">9. Contact Us</h2>
                    <p>
                        If you have any questions about this Privacy Policy, please contact
                        us at: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
                    </p>
                </div>
            </div>
        </section>
    );
}

export default PrivacyPageComponent;